import { z } from "zod";

/**
 * Two-step sign-in (TOTP) and staff step-up confirmation (docs/08 section 3; ADR-0039).
 *
 * - Every account may turn on two-step sign-in with an authenticator app (Better Auth's two-factor plugin). Accounts
 *   holding a staff role MUST have it to use any staff permission (MFA_REQUIRED otherwise), and the session they use
 *   must have proved the second factor (a code at sign-in, at enrolment or a confirmation).
 * - `dangerous` permissions also need a recent confirmation: the staff member enters a current authenticator code
 *   (POST /me/two-factor/step-up) and the server records it against THIS session (never a client flag) and audits it.
 *   It lasts STAFF_STEP_UP_MINUTES (default 10).
 * - The browser never decides any of this: the server answers MFA_REQUIRED / STEP_UP_REQUIRED and the web app shows the
 *   set-up panel or the "enter your code" dialog, then retries the action once.
 */

/** Where staff set up two-step sign-in (the account page section). */
export const TWO_STEP_SETUP_PATH = "/account#two-step";

/** A current code from an authenticator app: 6 digits (TOTP, 30-second steps). */
export const TOTP_CODE_RE = /^\d{6}$/;
/** A backup code as Better Auth issues them: 10 letters/digits shown as `abcde-12345`. */
export const BACKUP_CODE_RE = /^[A-Za-z0-9]{5}-[A-Za-z0-9]{5}$/;
/** Backup codes issued at enrolment (each works once). */
export const BACKUP_CODE_COUNT = 10;

/** Spaces and dashes people type inside a 6-digit code ("123 456") are ignored. */
export function normalizeTotpCode(raw: string): string {
  return raw.replace(/[\s-]/g, "");
}

/** Backup codes are case-sensitive; only surrounding spaces are dropped. */
export function normalizeBackupCode(raw: string): string {
  return raw.trim();
}

export const TotpCode = z
  .string()
  .max(20)
  .transform(normalizeTotpCode)
  .pipe(z.string().regex(TOTP_CODE_RE, "Enter the 6-digit code from your authenticator app."));

/** Why a staff request was refused for assurance (details.reason of STEP_UP_REQUIRED). */
export const StepUpReason = z.enum([
  /** This session has not proved the second factor (it was created before two-step sign-in was turned on, ...). */
  "session_not_verified",
  /** A `dangerous` permission and no confirmation on this session yet. */
  "step_up_required",
  /** A `dangerous` permission and the last confirmation is older than the window. */
  "step_up_expired",
]);
export type StepUpReason = z.infer<typeof StepUpReason>;

/** details of a STEP_UP_REQUIRED error. */
export const StepUpRequiredDetails = z.object({ reason: StepUpReason, windowMinutes: z.number().int().positive() });
export type StepUpRequiredDetails = z.infer<typeof StepUpRequiredDetails>;

/** details of an MFA_REQUIRED error. */
export const MfaRequiredDetails = z.object({ setupPath: z.string() });
export type MfaRequiredDetails = z.infer<typeof MfaRequiredDetails>;

/** GET /me/two-factor: the signed-in account's own two-step sign-in state (for the account page and staff screens). */
export const TwoFactorStatusResponse = z.object({
  /** Two-step sign-in is on (a verified authenticator). */
  enabled: z.boolean(),
  /** Set-up was started but the first code was never confirmed (nothing is enforced yet). */
  setupPending: z.boolean(),
  /** Unused backup codes, when enabled. */
  backupCodesLeft: z.number().int().min(0).nullable(),
  /** Wrong codes locked further attempts until this time (ISO UTC), or null. */
  lockedUntil: z.string().nullable(),
  thisSession: z.object({
    /** This session proved the second factor (code at sign-in, at set-up, or a confirmation). */
    verified: z.boolean(),
    /** A staff confirmation on this session is valid until (ISO UTC), or null. */
    stepUpUntil: z.string().nullable(),
  }),
  staff: z.object({
    /** The account holds at least one staff permission. */
    hasStaffRole: z.boolean(),
    /** Staff tools need two-step sign-in in this environment (STAFF_MFA_REQUIRED). */
    required: z.boolean(),
    /** How long a confirmation lasts for `dangerous` staff actions. */
    stepUpMinutes: z.number().int().positive(),
  }),
});
export type TwoFactorStatusResponse = z.infer<typeof TwoFactorStatusResponse>;

/** POST /me/two-factor/step-up: confirm with a current authenticator code (staff only). */
export const StepUpRequest = z.object({ code: TotpCode });
export type StepUpRequest = z.infer<typeof StepUpRequest>;

export const StepUpResponse = z.object({
  /** The confirmation is valid on this session until (ISO UTC). */
  stepUpUntil: z.string(),
  windowMinutes: z.number().int().positive(),
});
export type StepUpResponse = z.infer<typeof StepUpResponse>;

/** POST /me/two-factor/disable: the password AND a current authenticator code or an unused backup code. */
export const TwoFactorDisableRequest = z.object({
  password: z.string().min(1, "Enter your password.").max(128),
  code: z.string().trim().min(1, "Enter a code from your authenticator app or a backup code.").max(40),
});
export type TwoFactorDisableRequest = z.infer<typeof TwoFactorDisableRequest>;

export const TwoFactorDisableResponse = z.object({ enabled: z.literal(false) });
export type TwoFactorDisableResponse = z.infer<typeof TwoFactorDisableResponse>;
